import React, { useEffect, useMemo, useState } from 'react';
import {
    Input, Checkbox, Collapse, Space, Button, Tag, Typography, Spin, Empty, Alert
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { testService } from '../services/testService';
import './LabTestLibraryPicker.css';

const { Text } = Typography;

// Which investigations this doctor wants offered when they order a test.
//
// The library is every test the app knows about, which is far more than any one
// doctor uses - a urologist has no use for 27 obstetric assays, and an Obs & Gyn
// has none for PSA. So the doctor switches on what their speciality actually
// requests, once, and their order form is theirs from then on.
//
// Nothing here deletes anything: the library is shared and read-only, and a test
// switched off is switched off for this doctor alone.
//
// Controlled: `value` is an array of test codes, `onChange` is called with the
// new array. Lives inside a Form.Item on the settings page.
//
// `value === undefined` means the doctor has never been to this screen, which is
// read as "everything is offered" - the same rule the visit-form fields go by,
// and the reason an untouched account has a working order form rather than an
// empty one.
const LabTestLibraryPicker = ({ value, onChange }) => {
    const { t } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    // Kept in state rather than left to antd's uncontrolled default: a search
    // opens every matching group, and the panel has to stay controlled across
    // that switch or React warns about it changing modes mid-life.
    const [openKeys, setOpenKeys] = useState([]);

    useEffect(() => {
        let cancelled = false;

        testService.getLibrary()
            .then((response) => {
                if (cancelled) return;
                setCategories(response.data || []);
                setError(null);
            })
            .catch((fetchError) => {
                if (!cancelled) setError(fetchError.message);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    const allCodes = useMemo(
        () => categories.flatMap((category) => category.tests.map((test) => test.code)),
        [categories]
    );

    // The unconfigured state is "all of them". Resolved here rather than in the
    // parent so the parent only ever has to store what the doctor actually chose.
    const notConfigured = !Array.isArray(value);
    const selected = useMemo(
        () => new Set(notConfigured ? allCodes : value),
        [notConfigured, allCodes, value]
    );

    const emit = (nextSet) => onChange && onChange([...nextSet]);

    const toggleTest = (code, checked) => {
        const next = new Set(selected);
        if (checked) next.add(code); else next.delete(code);
        emit(next);
    };

    const toggleCategory = (category, checked) => {
        const next = new Set(selected);
        category.tests.forEach((test) => {
            if (checked) next.add(test.code); else next.delete(test.code);
        });
        emit(next);
    };

    const term = search.trim().toLowerCase();

    // Searching narrows what is shown, never what is selected - a doctor who
    // searches "thyroid", ticks two tests and clears the box must not find the
    // rest of their list gone.
    const visible = useMemo(() => {
        if (!term) return categories;
        return categories
            .map((category) => ({
                ...category,
                tests: category.tests.filter((test) =>
                    test.name.toLowerCase().includes(term) ||
                    category.label.toLowerCase().includes(term)
                )
            }))
            .filter((category) => category.tests.length > 0);
    }, [categories, term]);

    if (loading) {
        return <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>;
    }

    if (error) {
        return <Alert type="warning" showIcon message={t('error')} description={error} />;
    }

    const items = visible.map((category) => {
        const codes = category.tests.map((test) => test.code);
        const onCount = codes.filter((code) => selected.has(code)).length;

        return {
            key: category.key,
            label: (
                <Space size={8} wrap>
                    <Text strong>{category.label}</Text>
                    <Tag color={onCount === 0 ? 'default' : 'blue'}>
                        {onCount} / {category.tests.length}
                    </Tag>
                </Space>
            ),
            extra: (
                // Stops the click from also collapsing the panel it sits on.
                <span onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                        checked={onCount === codes.length}
                        indeterminate={onCount > 0 && onCount < codes.length}
                        onChange={(event) => toggleCategory(category, event.target.checked)}
                    >
                        {t('selectAll') || 'All'}
                    </Checkbox>
                </span>
            ),
            children: (
                <div className="lab-library-grid">
                    {category.tests.map((test) => (
                        <Checkbox
                            key={test.code}
                            checked={selected.has(test.code)}
                            onChange={(event) => toggleTest(test.code, event.target.checked)}
                        >
                            <span title={test.referenceRange || undefined}>{test.name}</span>
                        </Checkbox>
                    ))}
                </div>
            )
        };
    });

    return (
        <div>
            <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={t('searchTests') || 'Search tests'}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    style={{ width: 260 }}
                />
                <Space wrap>
                    <Text type="secondary">
                        {selected.size} / {allCodes.length} {t('selectedCount')}
                    </Text>
                    <Button size="small" onClick={() => emit(new Set(allCodes))}>
                        {t('selectAll') || 'Select all'}
                    </Button>
                    <Button size="small" onClick={() => emit(new Set())}>
                        {t('clearAll') || 'Clear all'}
                    </Button>
                </Space>
            </Space>

            {items.length === 0
                ? <Empty description={t('noTestsFound') || 'No tests match that search'} />
                : (
                    <Collapse
                        items={items}
                        // While a search is running every matching group is open:
                        // the matches are the point, and hunting for them behind
                        // closed panels defeats the search.
                        activeKey={term ? visible.map((category) => category.key) : openKeys}
                        onChange={(keys) => { if (!term) setOpenKeys(keys); }}
                        size="small"
                    />
                )}
        </div>
    );
};

export default LabTestLibraryPicker;
